import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, message } from "antd";
import { EyeOutlined, UploadOutlined } from "@ant-design/icons";
import FilePreviewModal from "../PreAuth-query/FilePreviewModal";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  isAllowedDocumentUpload,
  revokeObjectUrl,
} from "../patient-list/documentUploadPreviewUtils";
import { fetchDocumentViewUrl } from "../../../apis/documentService";
import {
  fetchQueryDocumentPreviewBlob,
  fetchQueryDocumentViewUrl,
} from "../../../apis/hmoQueryService";
import type { QueryUploadDocument } from "../../../utils/queryDocumentUtils";
import {
  createQueryUploadEntry,
  isUsableQueryDocumentViewUrl,
  mergeQueryUpload,
  queryUploadMatchesRow,
  resolveQueryDocumentMasterName,
  resolveQueryUploadPreviewUrl,
} from "../../../utils/queryDocumentUtils";

export type QueryDocumentUploadRow = {
  key: string;
  label: string;
  documentType: string;
  required?: boolean;
};

type QueryResponseDocumentsUploadProps = {
  rows: QueryDocumentUploadRow[];
  uploads: QueryUploadDocument[];
  onConfirmUpload?: (
    row: QueryDocumentUploadRow,
    file: File,
  ) => Promise<boolean>;
  /** Stage file locally without calling the API (used when onConfirmUpload is not set). */
  onUpload?: (row: QueryDocumentUploadRow, file: File) => void;
  disabled?: boolean;
};

type PendingUpload = {
  row: QueryDocumentUploadRow;
  file: File;
  fileUrl: string;
  fileName: string;
};

const QueryResponseDocumentsUpload: FC<QueryResponseDocumentsUploadProps> = ({
  rows,
  uploads,
  onConfirmUpload,
  onUpload,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRowRef = useRef<QueryDocumentUploadRow | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [pendingPreviewVisible, setPendingPreviewVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [localUploads, setLocalUploads] = useState<QueryUploadDocument[]>([]);
  const [viewPreview, setViewPreview] = useState<{
    url: string;
    fileName: string;
  } | null>(null);

  const clearPendingUpload = useCallback(() => {
    setPendingPreviewVisible(false);
    setPendingUpload((current) => {
      revokeObjectUrl(current?.fileUrl);
      return null;
    });
  }, []);

  useEffect(
    () => () => {
      clearPendingUpload();
      localUploads.forEach((doc) => revokeObjectUrl(doc.documentUrl));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const mergedUploads = useMemo(() => {
    let merged = [...uploads];
    for (const local of localUploads) {
      merged = mergeQueryUpload(merged, local);
    }
    return merged;
  }, [localUploads, uploads]);

  if (!rows.length) return null;

  const findUpload = (row: QueryDocumentUploadRow) =>
    mergedUploads?.find((doc) => queryUploadMatchesRow(doc, row));

  const openFilePicker = (row: QueryDocumentUploadRow) => {
    if (disabled || confirmLoading || viewLoading) return;
    pendingRowRef.current = row;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const row = pendingRowRef.current;
    pendingRowRef.current = null;
    if (!file || !row) return;

    if (!isAllowedDocumentUpload(file)) {
      message.error("Only PDF and image files (JPG, PNG, WEBP) are allowed.");
      return;
    }

    clearPendingUpload();
    setPendingUpload({
      row,
      file,
      fileUrl: URL.createObjectURL(file),
      fileName: file.name,
    });
    setPendingPreviewVisible(true);
  };

  const handleConfirmUpload = async () => {
    if (!pendingUpload) return;
    setConfirmLoading(true);
    try {
      let uploaded = false;
      if (onConfirmUpload) {
        uploaded = await onConfirmUpload(
          pendingUpload.row,
          pendingUpload.file,
        );
      } else if (onUpload) {
        onUpload(pendingUpload.row, pendingUpload.file);
        setLocalUploads((prev) =>
          mergeQueryUpload(
            prev,
            createQueryUploadEntry(
              pendingUpload.file,
              pendingUpload.row.documentType,
              pendingUpload.row.label,
            ),
          ),
        );
        uploaded = true;
      }
      if (uploaded) {
        clearPendingUpload();
        message.success("Document attached.");
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleView = async (
    row: QueryDocumentUploadRow,
    uploaded: QueryUploadDocument,
  ) => {
    setViewLoading(true);
    try {
      const localUrl = resolveQueryUploadPreviewUrl(uploaded);
      if (localUrl) {
        setViewPreview({
          url: localUrl,
          fileName: uploaded.documentName || row.label,
        });
        return;
      }

      const cachedViewUrl = uploaded.viewUrl?.trim();
      if (isUsableQueryDocumentViewUrl(cachedViewUrl)) {
        setViewPreview({
          url: cachedViewUrl as string,
          fileName: uploaded.documentName || row.label,
        });
        return;
      }

      if (uploaded.id) {
        try {
          const queryDoc = await fetchQueryDocumentViewUrl(uploaded.id);
          if (isUsableQueryDocumentViewUrl(queryDoc.viewUrl)) {
            setViewPreview({
              url: queryDoc.viewUrl as string,
              fileName: queryDoc.documentName || uploaded.documentName || row.label,
            });
            return;
          }
          const preview = await fetchQueryDocumentPreviewBlob(uploaded.id);
          setViewPreview({
            url: preview.blobUrl,
            fileName: queryDoc.documentName || uploaded.documentName || row.label,
          });
          return;
        } catch {
          // Fall back to BPA document view for stage-linked documents.
        }

        try {
          const res = await fetchDocumentViewUrl(uploaded.id);
          setViewPreview({
            url: res.viewUrl,
            fileName: res.fileName || uploaded.documentName || row.label,
          });
          return;
        } catch {
          // handled below
        }
      }

      message.error("Unable to open document.");
    } catch {
      message.error("Unable to open document.");
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <>
      <div className="qr-docs-upload">
        <div className="qr-docs-upload__title">Upload Required Documents</div>
        <div className="qr-docs-upload__list">
          {rows.map((row) => {
            const uploaded = findUpload(row);
            return (
              <div key={row.key} className="qr-docs-upload__row">
                <span className="qr-docs-upload__label">
                  {row.label}
                  {row.required ? <span className="qr-docs-upload__req"> *</span> : null}
                </span>
                <div className="qr-docs-upload__actions">
                  {uploaded ? (
                    <span
                      className="qr-docs-upload__filename qr-docs-upload__filename--new"
                      title={uploaded.documentName}
                    >
                      {uploaded.documentName}
                    </span>
                  ) : (
                    <span className="qr-docs-upload__placeholder">No file selected</span>
                  )}
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    disabled={disabled || confirmLoading || viewLoading}
                    loading={confirmLoading}
                    onClick={() => openFilePicker(row)}
                  >
                    {uploaded ? "Replace" : "Upload"}
                  </Button>
                  {uploaded ? (
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      loading={viewLoading}
                      onClick={() => handleView(row, uploaded)}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        hidden
        onChange={handleFileSelected}
      />

      {pendingUpload ? (
        <FilePreviewModal
          isVisible={pendingPreviewVisible}
          onClose={clearPendingUpload}
          fileUrl={pendingUpload.fileUrl}
          fileName={pendingUpload.fileName}
          handleUpload={handleConfirmUpload}
          showUploadButton
          isLoading={confirmLoading}
        />
      ) : null}

      {viewPreview ? (
        <FilePreviewModal
          isVisible={Boolean(viewPreview)}
          onClose={() => setViewPreview(null)}
          fileUrl={viewPreview.url}
          fileName={viewPreview.fileName}
          handleUpload={() => undefined}
          showUploadButton={false}
        />
      ) : null}
    </>
  );
};

export const buildRequiredDocumentRows = (
  requiredDocuments?: { id?: number; name: string }[] | null,
): QueryDocumentUploadRow[] =>
  (requiredDocuments || []).map((doc, index) => ({
    key: String(doc.id ?? `${doc.name}-${index}`),
    label: doc.name,
    documentType: resolveQueryDocumentMasterName(doc.name),
    required: true,
  }));

export const buildDeficiencyDocumentRow = (
  oldDocument?: {
    documentLabelType?: string;
    documentType?: string;
  } | null,
): QueryDocumentUploadRow[] => {
  if (!oldDocument?.documentLabelType && !oldDocument?.documentType) return [];
  const label = oldDocument.documentLabelType || oldDocument.documentType || "Document";
  return [
    {
      key: "deficiency-doc",
      label,
      documentType: resolveQueryDocumentMasterName(
        oldDocument.documentType || label,
      ),
      required: true,
    },
  ];
};

export default QueryResponseDocumentsUpload;
